// ChatBoxMiddle.jsx (Better‑than‑FAANG+ voice memo + rich attachment preview)
// Backward compatible: existing props still work; new features additive.
import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import './chatBoxMiddle.css';

const ChatBoxMiddle = forwardRef(function ChatBoxMiddle({
  dismissiblePreview = false,
  // Existing props
  isTypingDisplay,
  senderPlayerId,
  status,
  mediaBlobUrl,
  loading,
  handleMicClick,
  isRecording,
  setVoiceMemo,
  clearBlobUrl,
  handleSendAudioMemo,
  fileInput,
  handleFileInput, // legacy: called on <input> change
  error,
  setError,
  file,
  setFile,
  attachChatFile,
  clearChatAttachment,

  // New optional props
  onSendFile, // (file) => Promise|void
  fileSending = false, // parent controls sending state
  // NEW optional flag
  autoClearOnSend = true,
  autoSendOnSelect = false, // if true, immediately calls onSendFile / handleFileInput
  showFileSend = true,
  accept = undefined, // override accept types
  maxFileMB = 200, // soft limit (no change to upload logic)
  onFileRejected, // (reason, file)
  showFileSize = true,
  transformFile, // async (file) => newFile (e.g. image compression)
  // NEW: parent message submit (fallback when onSendFile not supplied)
  handleSubmit,
}, ref) {
  const audioRef = useRef(null);

  // Attachment preview state
  const [localFile, setLocalFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [localSending, setLocalSending] = useState(false);
  const prevLoadingRef = useRef(loading);
  const prevFileSendingRef = useRef(fileSending);
  const effectiveSending = fileSending || localSending;
  const hasControlledFile = typeof setFile === 'function';
  const selectedFile = hasControlledFile ? file || null : localFile;

  useEffect(() => { setPreviewCollapsed(false); }, [selectedFile]);
  useImperativeHandle(ref, () => ({ dismissPreview: () => {
    if (!dismissiblePreview || !selectedFile || previewCollapsed) return false;
    setPreviewCollapsed(true);
    return true;
  } }));

  // ========== Voice Memo Helpers ==========

  const cancelVoice = () => {
    try {
      if (isRecording) handleMicClick?.();
    } catch {}
    clearBlobUrl?.();
    setVoiceMemo?.(false);
  };

  const reRecord = () => {
    clearBlobUrl?.();
    setVoiceMemo?.(true);
    if (!isRecording) handleMicClick?.();
  };

  const endRecordOrStart = () => {
    // When recording -> stop; else toggles (used by Stop button)
    handleMicClick?.();
  };

  // ========== File Handling ==========
  const revokePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => () => revokePreview(), [revokePreview]);

  const buildPreview = useCallback((file) => {
    if (!file) return;
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } else {
      // Non media – no preview URL needed
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return '';
      });
    }
  }, []);

  useEffect(() => {
    if (selectedFile) {
      buildPreview(selectedFile);
      return;
    }
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return '';
    });
  }, [buildPreview, selectedFile]);

  const resetFile = useCallback(() => {
    revokePreview();
    setPreviewUrl('');
    setLocalFile(null);
    if (typeof clearChatAttachment === 'function') {
      clearChatAttachment();
    } else if (hasControlledFile) {
      setFile(null);
    }
    if (fileInput?.current) fileInput.current.value = '';
  }, [clearChatAttachment, fileInput, hasControlledFile, revokePreview, setFile]);

  const rejectFile = (reason, file) => {
    resetFile();
    onFileRejected?.(reason, file);
    setError?.(reason);
  };

  const attachIncomingFile = async (incomingFile, { source = 'picker' } = {}) => {
    if (!incomingFile) {
      return {
        accepted: false,
        errorCode: 'NO_FILE',
        file: null,
      };
    }
    if (incomingFile.size / 1024 / 1024 > maxFileMB) {
      const message = `File exceeds ${maxFileMB}MB limit`;
      rejectFile(message, incomingFile);
      return {
        accepted: false,
        errorCode: 'TOO_LARGE',
        file: null,
        message,
      };
    }
    let eff = incomingFile;
    try {
      if (transformFile) {
        const maybeNew = await transformFile(incomingFile);
        if (maybeNew instanceof File || maybeNew instanceof Blob) {
          eff = maybeNew;
        }
      }
    } catch (e) {
      // silent; keep original
    }
    const result =
      typeof attachChatFile === 'function'
        ? attachChatFile(eff, { source })
        : handleFileInput?.({ target: { files: [eff] } });

    if (result?.accepted === false) {
      setLocalFile(null);
      return result;
    }

    if (hasControlledFile) {
      setLocalFile(null);
    } else {
      setLocalFile(result?.file || eff);
    }

    return {
      accepted: true,
      errorCode: null,
      file: result?.file || eff,
    };
  };

  const onLocalFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      resetFile();
      handleFileInput?.(e); // legacy pass-through
      return;
    }
    const result = await attachIncomingFile(file, { source: 'picker' });

    if (autoSendOnSelect && result?.accepted) {
      attemptSendFile(result.file || file);
    }
  };

  const attemptSendFile = useCallback(
    async (file = selectedFile) => {
      if (!file || effectiveSending) return;
      setError?.('');
      // Path 1: explicit onSendFile prop handles the upload/send
      if (onSendFile) {
        try {
          setLocalSending(true);
          await onSendFile(file);
          resetFile();
        } catch (e) {
          setError?.(e?.message || 'Failed to send file');
        } finally {
          setLocalSending(false);
        }
        return;
      }
      // Path 2: fall back to parent chat message submit (uses parent file state)
      if (handleSubmit) {
        handleSubmit({ type: 'click', meta: 'previewSend' });
      }
      // Do NOT reset here; parent will clear file after successful send
    },
    [selectedFile, onSendFile, effectiveSending, handleSubmit, setError, resetFile],
  );

  // Auto clear after external send completes (Enter / parent-controlled)
  useEffect(() => {
    if (!autoClearOnSend || hasControlledFile) {
      prevLoadingRef.current = loading;
      prevFileSendingRef.current = fileSending;
      return;
    }
    const loadingFinished = prevLoadingRef.current && !loading; // transition true -> false
    const fileSendingFinished = prevFileSendingRef.current && !fileSending; // transition true -> false

    if ((loadingFinished || fileSendingFinished) && selectedFile) {
      resetFile();
    }
    prevLoadingRef.current = loading;
    prevFileSendingRef.current = fileSending;
  }, [
    loading,
    fileSending,
    selectedFile,
    autoClearOnSend,
    hasControlledFile,
    resetFile,
  ]);

  // ========== Typing / Voice Effect ==========
  useEffect(() => {
    if (mediaBlobUrl && audioRef.current) audioRef.current.load();
  }, [mediaBlobUrl]);

  // ========== Derived ==========
  const isImage = !!selectedFile && selectedFile.type.startsWith('image/');
  const isVideo = !!selectedFile && selectedFile.type.startsWith('video/');
  const isGeneric = selectedFile && !isImage && !isVideo;

  return (
    <div className="cbMiddleWrapper">
      {/* Typing Indicator */}
      <div
        className={
          'cbTypingReserve ' +
          (isTypingDisplay && senderPlayerId ? 'hasTyping' : '')
        }
        aria-live="polite"
        aria-hidden={isTypingDisplay ? 'false' : 'true'}
      >
        {isTypingDisplay && senderPlayerId && (
          <div className="cbTyping" role="status" aria-live="polite">
            <span className="cbDot" />
            <span className="cbDot" />
            <span className="cbDot" />
            <span className="cbTypingLabel">{senderPlayerId} typing</span>
          </div>
        )}
      </div>
      {/* Voice Memo Panel */}
      {(isRecording || mediaBlobUrl) && (
        <div
          className={
            'cbVMPanel ' + (isRecording ? 'cbVMRecording' : 'cbVMReview')
          }
        >
          <div className="cbVMLeft">
            {isRecording ? (
              <>
                <span className="cbVMRecDot" aria-label="Recording" />
                <span className="cbVMRecText">
                  Recording…{' '}
                  {status && status !== 'recording' ? `(${status})` : ''}
                </span>
              </>
            ) : (
              <>
                <span className="cbVMReadyIcon">✔</span>
                <span className="cbVMReadyText">Preview</span>
              </>
            )}
          </div>

          <div className="cbVMCenter">
            {mediaBlobUrl && !isRecording && (
              <audio
                ref={audioRef}
                className="cbVMAudio"
                controls
                src={mediaBlobUrl}
                aria-label="Voice memo playback"
              />
            )}
            {isRecording && (
              <div className="cbVMWave" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            )}
          </div>

          <div className="cbVMButtons">
            {isRecording ? (
              <button
                type="button"
                className="cbBtn cbStop"
                onClick={endRecordOrStart}
              >
                Stop
              </button>
            ) : (
              mediaBlobUrl && (
                <button
                  type="button"
                  className="cbBtn cbReRecord"
                  onClick={reRecord}
                >
                  Re‑record
                </button>
              )
            )}
            <button
              type="button"
              className="cbBtn cbCancel"
              onClick={cancelVoice}
            >
              Cancel
            </button>
            {!isRecording && mediaBlobUrl && (
              <button
                type="button"
                className="cbBtn cbSend"
                onClick={handleSendAudioMemo}
              >
                Send
              </button>
            )}
          </div>
        </div>
      )}

      {/* File Preview (new) */}
      {selectedFile && dismissiblePreview && previewCollapsed && (
        <button type="button" onClick={() => setPreviewCollapsed(false)}>Show attachment preview</button>
      )}
      {selectedFile && !(dismissiblePreview && previewCollapsed) && (
        <div
          className="cbFilePreview"
          role="group"
          aria-label="Selected file preview"
        >
          <div className="cbFileMedia">
            {isImage && (
              <img
                src={previewUrl}
                alt={selectedFile.name}
                className="cbFileImg"
                draggable="false"
              />
            )}
            {isVideo && (
              <video
                src={previewUrl}
                className="cbFileVideo"
                controls
                playsInline
              />
            )}
            {isGeneric && (
              <div className="cbFileGeneric">
                <span className="cbFileExt">
                  {selectedFile.name.split('.').pop()?.toUpperCase() || 'FILE'}
                </span>
              </div>
            )}
          </div>
          <div className="cbFileMeta">
            <div className="cbFileName" title={selectedFile.name}>
              {selectedFile.name}
            </div>
            {showFileSize && (
              <div className="cbFileSize">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}
          </div>
          <div className="cbFileActions">
            <button
              type="button"
              className="cbBtn cbFileRemove"
              onClick={resetFile}
              disabled={effectiveSending}
              aria-label="Remove file"
            >
              ✕
            </button>
            {!autoSendOnSelect && showFileSend && (
              <button
                type="button"
                className="cbBtn cbFileSend"
                onClick={() => attemptSendFile()}
                disabled={effectiveSending}
              >
                {effectiveSending ? 'Sending…' : 'Send'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Hidden file input (backward compatible: still calls handleFileInput) */}
      <input
        ref={fileInput}
        type="file"
        hidden
        onChange={onLocalFileChange}
        accept={accept}
        aria-hidden="true"
      />

      {/* Legacy loader */}
      {/* {loading && (
        <div className="cbLoading">
          <ClipLoader size={16} color="#1e7bff" /> <span>Uploading…</span>
        </div>
      )} */}

      {error && (
        <div className="cbError" role="alert">
          {error}
          <button
            type="button"
            className="cbErrClose"
            onClick={() => setError?.('')}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
});
export default ChatBoxMiddle;
